const WEEK_START_DAYS = {
    // Nordic countries
    SE: 1,
    DK: 1,
    NO: 1,
    FI: 1,
    // Western Europe
    DE: 1,
    NL: 1,
    BE: 1,
    FR: 1,
    GB: 0,
    IE: 0,
    CH: 1,
    AT: 1,
    // Southern Europe
    IT: 0,
    ES: 1,
    PT: 0,
    GR: 1,
    // Eastern Europe
    PL: 1,
    CZ: 1,
    HU: 1,
    RO: 1,
    SK: 1,
    BG: 1,
    HR: 1,
    RS: 1,
    LT: 1,
    SI: 1,
    // North America
    US: 0,
    CA: 0,
    // Asia
    CN: 0,
    JP: 0,
    KR: 0,
    IN: 0,
    // Oceania
    AU: 1,
    NZ: 1, // New Zealand
};
export function getWeekdayRegionOffs(locale) {
    const region = locale.includes("-")
        ? locale.split("-")[1]?.toUpperCase()
        : locale.toUpperCase();
    // 0 = Sunday, 1 = Monday (default), 6 = Saturday
    // WEEK_START_DAYS is a map of alpha-2 country code to JS weekday (0-6)
    return (WEEK_START_DAYS[region] ??
        1);
}
/** Normalises a Date to UTC midnight using its UTC calendar components. */
export function toUtcCalendarDate(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
/**
 * Snaps a calendar date to the start of its week in UTC.
 * Matches forecast-server week keys (see getWeekStartDate in x-forecast-server).
 */
export function snapToUtcWeekStart(date, weekStartsOn) {
    const d = toUtcCalendarDate(date);
    d.setUTCHours(0, 0, 0, 0);
    let diff = d.getUTCDay() - weekStartsOn;
    if (diff < 0) {
        diff += 7;
    }
    d.setUTCDate(d.getUTCDate() - diff);
    return d;
}
export function formatUtcDateKey(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
/** Maps a yyyy-MM-dd string or Date to the forecast API week-start key (UTC). */
export function getUtcWeekStartKey(date, weekStartsOn) {
    const calendarDate = typeof date === "string"
        ? (() => {
            const [year, month, day] = date.split("-").map(Number);
            return new Date(Date.UTC(year, month - 1, day));
        })()
        : toUtcCalendarDate(date);
    return formatUtcDateKey(snapToUtcWeekStart(calendarDate, weekStartsOn));
}
/** Adds calendar days in UTC without local timezone drift. */
export function addUtcDays(date, days) {
    const next = toUtcCalendarDate(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}
export function buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn) {
    const normalizedFiscalStart = toUtcCalendarDate(fiscalStart);
    const fiscalEndUtc = toUtcCalendarDate(fiscalEnd);
    const weeks = [normalizedFiscalStart];
    let weekStart = snapToUtcWeekStart(addUtcDays(normalizedFiscalStart, 7), weekStartsOn);
    for (let guard = 0; guard < 60; guard++) {
        if (weekStart.getTime() > fiscalEndUtc.getTime()) {
            break;
        }
        weeks.push(weekStart);
        weekStart = snapToUtcWeekStart(addUtcDays(weekStart, 7), weekStartsOn);
    }
    return weeks;
}
/**
 * Resolves the IKEA fiscal week start for a date.
 * Week 1 always begins on Sep 1; subsequent weeks follow country calendar alignment.
 */
export function resolveFiscalWeekStart(date, fiscalStart, fiscalEnd, weekStartsOn) {
    const calendarDate = toUtcCalendarDate(date);
    const weekStarts = buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn);
    let resolved = weekStarts[0] ?? toUtcCalendarDate(fiscalStart);
    for (const weekStart of weekStarts) {
        if (weekStart.getTime() <= calendarDate.getTime()) {
            resolved = weekStart;
        }
        else {
            break;
        }
    }
    return resolved;
}
/**
 * Resolves the inclusive end date of a fiscal week.
 * The last week of the fiscal year always ends on Aug 31.
 */
export function resolveFiscalWeekEnd(weekStart, fiscalStart, fiscalEnd, weekStartsOn) {
    const normalizedWeekStart = toUtcCalendarDate(weekStart);
    const normalizedFiscalEnd = toUtcCalendarDate(fiscalEnd);
    const weekStarts = buildFiscalWeekStarts(fiscalStart, fiscalEnd, weekStartsOn);
    const index = weekStarts.findIndex((candidate) => candidate.getTime() === normalizedWeekStart.getTime());
    if (index === -1) {
        const fallbackEnd = addUtcDays(normalizedWeekStart, 6);
        return fallbackEnd.getTime() > normalizedFiscalEnd.getTime()
            ? normalizedFiscalEnd
            : fallbackEnd;
    }
    const nextWeekStart = weekStarts[index + 1];
    if (nextWeekStart !== undefined) {
        const candidateEnd = addUtcDays(nextWeekStart, -1);
        return candidateEnd.getTime() > normalizedFiscalEnd.getTime()
            ? normalizedFiscalEnd
            : candidateEnd;
    }
    return normalizedFiscalEnd;
}
export function resolveFiscalWeekWindow(date, fiscalStart, fiscalEnd, weekStartsOn) {
    const weekStart = resolveFiscalWeekStart(date, fiscalStart, fiscalEnd, weekStartsOn);
    const weekEnd = resolveFiscalWeekEnd(weekStart, fiscalStart, fiscalEnd, weekStartsOn);
    return { weekStart, weekEnd };
}
//# sourceMappingURL=date-utils.mjs.map